import React from 'react';
import { RiProfileLine } from 'react-icons/ri';
import { PiPlugsConnectedLight, PiUser } from 'react-icons/pi';
import { LuSendHorizontal, LuThumbsUp, LuMail, LuCalendarCheck } from 'react-icons/lu';
import { HiOutlineClock, HiSpeakerWave } from 'react-icons/hi2';
import { BsChatRightText } from 'react-icons/bs';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';
import { TbHourglassEmpty } from 'react-icons/tb';
import { SiGooglegemini } from 'react-icons/si';

export const MESSAGE_VARIABLES = [
  { label: 'First Name', value: '{{first_name}}' },
  { label: 'Last Name', value: '{{last_name}}' },
  { label: 'Company Name', value: '{{company_name}}' },
  { label: 'Job Title', value: '{{job_title}}' },
  { label: 'Location', value: '{{location}}' },
];

export const MESSAGE_LIMITS = {
  send_connection: 200,
  send_message: 8000,
  comment_post: 1250,
};

export const ACTION_NODES = [
  {
    type: 'visit_profile',
    label: 'Visit Profile',
    icon: <RiProfileLine />,
    color: '#4DDBBA',
    bgColor: 'rgba(77, 219, 186, 0.1)',
  },
  {
    type: 'follow_user',
    label: 'Follow on LinkedIn',
    icon: <PiUser />,
    color: '#4ABFFF',
    bgColor: 'rgba(74, 191, 255, 0.1)',
  },
  {
    type: 'send_connection',
    label: 'Send Connection Request',
    icon: <PiPlugsConnectedLight />,
    color: '#FE8888',
    bgColor: 'rgba(254, 136, 136, 0.1)',
  },
  {
    type: 'send_message',
    label: 'Send Message',
    icon: <LuSendHorizontal />,
    color: '#DF99FF',
    bgColor: 'rgba(223, 153, 255, 0.1)',
  },
  {
    type: 'send_ai_email',
    label: 'Send AI Email',
    icon: <SiGooglegemini className="gradient-icon" />,
    color: '#8B5CF6',
    bgColor: 'rgba(139, 92, 246, 0.12)',
  },
  {
    type: 'send_linkedin_ai_message',
    label: 'Send AI LinkedIn Message',
    icon: <SiGooglegemini className="gradient-icon" />,
    color: '#8B5CF6',
    bgColor: 'rgba(139, 92, 246, 0.12)',
  },
  {
    type: 'send_voice_message',
    label: 'Send Voice Message',
    icon: <HiSpeakerWave />,
    color: '#4DDBBA',
    bgColor: 'rgba(77, 219, 186, 0.1)',
  },
  {
    type: 'like_post',
    label: 'Like Latest Post',
    icon: <LuThumbsUp />,
    color: '#FAA666',
    bgColor: 'rgba(250, 166, 102, 0.1)',
  },
  {
    type: 'comment_post',
    label: 'Comment on Latest Post',
    icon: <BsChatRightText />,
    color: '#4ABFFF',
    bgColor: 'rgba(74, 191, 255, 0.1)',
  },
  {
    type: 'check_connection',
    label: 'Check if Connected',
    icon: <IoIosCheckmarkCircleOutline />,
    color: '#22C55E',
    bgColor: 'rgba(34, 197, 94, 0.1)',
  },
  {
    type: 'wait_for_connection',
    label: 'Wait for Acceptance',
    icon: <HiOutlineClock />,
    color: '#FE8888',
    bgColor: 'rgba(254, 136, 136, 0.1)',
    requiresNodeType: 'send_connection',
  },
  {
    type: 'delay',
    label: 'Delay',
    icon: <TbHourglassEmpty />,
    color: '#2783EC',
    bgColor: 'rgba(39, 131, 236, 0.1)',
  },
  {
    type: 'send_email',
    label: 'Send Email',
    icon: <LuMail />,
    color: '#F59E0B',
    bgColor: 'rgba(245, 158, 11, 0.1)',
  },
  {
    type: 'check_email_status',
    label: 'Check Email Status',
    icon: <IoIosCheckmarkCircleOutline />,
    color: '#8B5CF6',
    bgColor: 'rgba(139, 92, 246, 0.1)',
    requiresNodeType: 'send_email',
  },
  {
    type: 'email_finder',
    label: 'Email Finder',
    icon: <IoIosCheckmarkCircleOutline />,
    color: '#0EA5E9',
    bgColor: 'rgba(14, 165, 233, 0.1)',
  },
  // {
  //   type: 'check_stripe_customer',
  //   label: 'Check Stripe Customer',
  //   icon: <IoIosCheckmarkCircleOutline />,
  //   color: '#635BFF',
  //   bgColor: 'rgba(99, 91, 255, 0.12)',
  // },
  {
    type: 'check_google_calendar_meeting',
    label: 'Check Google Calendar Meeting',
    icon: <LuCalendarCheck />,
    color: '#4285F4',
    bgColor: 'rgba(66, 133, 244, 0.12)',
  },
];

const BRANCH_KEYS = ['yesBranch', 'noBranch', 'repliedBranch', 'clickedBranch', 'openedBranch', 'nothingBranch'];

export const CASCADE_DELETE_NODE_TYPES = new Set(['send_connection', 'send_email']);

const countNodesInTree = nodes => {
  let count = 0;
  const walk = list => {
    (list || []).forEach(n => {
      count += 1;
      BRANCH_KEYS.forEach(k => {
        if (n.data?.[k]?.length) walk(n.data[k]);
      });
    });
  };
  walk(nodes);
  return count;
};

const findListContainingNode = (list, nodeId) => {
  const idx = (list || []).findIndex(n => n.id === nodeId);
  if (idx !== -1) return { list, index: idx };
  for (const n of list || []) {
    for (const k of BRANCH_KEYS) {
      if (n.data?.[k]?.length) {
        const found = findListContainingNode(n.data[k], nodeId);
        if (found) return found;
      }
    }
  }
  return null;
};

export const getCascadeDeleteImpact = (nodes, nodeId) => {
  const location = findListContainingNode(nodes, nodeId);
  if (!location) return null;

  const { list, index } = location;
  const node = list[index];
  if (!CASCADE_DELETE_NODE_TYPES.has(node.type)) {
    return { cascade: false, followingCount: 0, totalCount: 1 };
  }

  const totalCount = countNodesInTree(list.slice(index));
  const followingCount = totalCount - 1;
  return { cascade: followingCount > 0, followingCount, totalCount };
};

export const removeNodeAndFollowing = (nodes, nodeId) => {
  const idx = (nodes || []).findIndex(n => n.id === nodeId);
  if (idx !== -1) return nodes.slice(0, idx);

  return (nodes || []).map(n => {
    const data = { ...n.data };
    let changed = false;
    BRANCH_KEYS.forEach(k => {
      if (data[k] === undefined) return;
      const childIdx = data[k].findIndex(c => c.id === nodeId);
      if (childIdx !== -1) {
        data[k] = data[k].slice(0, childIdx);
        changed = true;
      } else {
        const next = removeNodeAndFollowing(data[k], nodeId);
        if (next !== data[k]) {
          data[k] = next;
          changed = true;
        }
      }
    });
    return changed ? { ...n, data } : n;
  });
};

export const getCascadeDeleteConfirmMessage = (nodeType, followingCount) => {
  const label = ACTION_NODES.find(a => a.type === nodeType)?.label || 'this step';
  const below =
    followingCount === 1 ? '1 additional step' : `${followingCount} additional steps`;
  return `Removing ${label} will also delete ${below} in this sequence. Are you sure you want to proceed?`;
};

export const workflowHasNodeType = (nodes, type) => {
  const walk = list =>
    (list || []).some(n => n.type === type || BRANCH_KEYS.some(k => walk(n.data?.[k])));
  return walk(nodes);
};

const EMAIL_STEP_TYPES = ['send_email', 'send_ai_email', 'check_email_status'];
const LINKEDIN_AI_STEP_TYPES = ['send_linkedin_ai_message'];

export const isActionNodeDisabled = (
  action,
  nodes,
  { hasLinkedInProfiles = true, hasEmailAccounts = true } = {},
) => {
  if (!hasLinkedInProfiles && LINKEDIN_AI_STEP_TYPES.includes(action.type)) return true;
  if (!hasEmailAccounts && EMAIL_STEP_TYPES.includes(action.type)) return true;
  return action.requiresNodeType && !workflowHasNodeType(nodes, action.requiresNodeType);
};

export const getDisabledActionTitle = (
  action,
  actionNodes,
  { hasLinkedInProfiles = true, hasEmailAccounts = true } = {},
) => {
  if (!hasLinkedInProfiles && action.type === 'send_linkedin_ai_message') {
    return 'Connect a LinkedIn account first to use Send AI LinkedIn Message.';
  }
  if (!hasEmailAccounts && action.type === 'send_email') {
    return 'Attach an email account first to use Send Email.';
  }
  if (!hasEmailAccounts && action.type === 'send_ai_email') {
    return 'Attach an email account first to use Send AI Email.';
  }
  if (!hasEmailAccounts && action.type === 'check_email_status') {
    return 'Attach an email account first, then add Send Email.';
  }
  if (!action.requiresNodeType) return undefined;
  const required = actionNodes.find(n => n.type === action.requiresNodeType);
  return `Add a ${required?.label || action.requiresNodeType} step to the workflow first`;
};

const removeNodesByTypes = (list, typesToRemove) => {
  const typeSet = new Set(typesToRemove);
  const walk = nodes =>
    (nodes || [])
      .filter(n => !typeSet.has(n.type))
      .map(n => {
        const hasBranches = BRANCH_KEYS.some(k => n.data?.[k] !== undefined);
        if (!hasBranches) return n;
        const data = { ...n.data };
        BRANCH_KEYS.forEach(k => {
          if (data[k] !== undefined) data[k] = walk(data[k]);
        });
        return { ...n, data };
      });
  return walk(list);
};

/** After deleting a prerequisite node, remove dependents if none remain (e.g. no send_email → drop check_email_status). */
export const pruneOrphanDependentNodes = (nodes, removedNodeType) => {
  if (!removedNodeType || workflowHasNodeType(nodes, removedNodeType)) return nodes;
  const dependentTypes = ACTION_NODES.filter(a => a.requiresNodeType === removedNodeType).map(a => a.type);
  return dependentTypes.length ? removeNodesByTypes(nodes, dependentTypes) : nodes;
};

/** Voice message step has recorded audio (matches backend: audioBase64 or audioUrl). */
export const hasVoiceMessageRecording = node => {
  const data = node?.data || {};
  return Boolean(
    (data.audioBase64 && String(data.audioBase64).trim()) ||
    (data.audioUrl && String(data.audioUrl).trim()),
  );
};

/**
 * Validation for a single workflow step before save or launch.
 * @param {'launch'|'save'} context - slightly different copy for voice message
 * @returns {{ title: string, message: string } | null}
 */
export const getNodeStepValidationError = (node, context = 'launch') => {
  if (!node?.type) return null;
  const { type, data = {} } = node;

  if (type === 'send_message' && !data.message?.trim()) {
    return {
      title: 'Message Content Required',
      message: 'Write your message in the Message Content.',
    };
  }

  if (type === 'send_ai_email') {
    if (!data.offerId) {
      return {
        title: 'Offer Required',
        message: 'Select an offer to use for AI email generation.',
      };
    }
    if (!data.emailAccountIds || data.emailAccountIds.length === 0) {
      return {
        title: 'Email Account Required',
        message: 'Select an account to send the AI-generated email from.',
      };
    }
  }

  if (type === 'send_linkedin_ai_message') {
    if (!data.offerId) {
      return {
        title: 'Offer Required',
        message: 'Select an offer to use for AI LinkedIn message generation.',
      };
    }
    if (!data.linkedinProfileIds || data.linkedinProfileIds.length === 0) {
      return {
        title: 'LinkedIn Profile Required',
        message: 'Select a LinkedIn profile to send the AI-generated message from.',
      };
    }
  }

  if (type === 'comment_post' && !data.message?.trim()) {
    return {
      title: 'Comment Content Required',
      message: 'Write your comment in the Comment Content.',
    };
  }

  if (type === 'send_voice_message' && !hasVoiceMessageRecording(node)) {
    return {
      title: 'Voice Recording Required',
      message:
        context === 'save'
          ? 'Record a voice message to save this step.'
          : 'Record a voice message in the Send Voice Message step.',
    };
  }

  if (type === 'send_email') {
    if (!data.emailAccountIds || data.emailAccountIds.length === 0) {
      return {
        title: 'Email Account Required',
        message: 'Select an account to send from in your Email step.',
      };
    }
    if (!data.emailSubject?.trim()) {
      return {
        title: 'Subject Required',
        message: 'Add a subject line to your Email step.',
      };
    }
    if (!data.emailBody?.trim()) {
      return {
        title: 'Email Body Required',
        message: 'Write the email content in your Email step.',
      };
    }
  }

  if (
    type === 'check_google_calendar_meeting' &&
    !data.googleCalendarAccountId &&
    !data.googleCalendarConnectedEmail
  ) {
    return {
      title: 'Google Calendar Account Required',
      message:
        context === 'save'
          ? 'Select a Google Calendar account to save this step.'
          : 'Please select a Google Calendar account before launching.',
    };
  }

  if (type === 'delay') {
    const delayAmount = Number(data.delayValue ?? data.delay);
    if (!Number.isFinite(delayAmount) || delayAmount < 1) {
      return {
        title: 'Delay Required',
        message: 'Set a delay duration in the Delay step.',
      };
    }
  }

  if (type === 'wait_for_connection') {
    const maxWaitTime = Number(data.maxWaitTime);
    if (!Number.isFinite(maxWaitTime) || maxWaitTime < 1) {
      return {
        title: 'Max Wait Time Required',
        message: 'Set a max wait time in the Wait for Acceptance step.',
      };
    }
  }

  return null;
};

export const MIN_ZOOM = 0.3;
export const MAX_ZOOM = 2;
export const ZOOM_STEP = 0.15;


export const leadsHubDummyData = [
  {
    "id": "80015",
    "person_email": "juan_cerrillo@jabil.com",
    "person_email_status": "Extrapolated",
    "first_name": "juan",
    "last_name": "cerrillo",
    "person_title": "produccion",
    "person_function": null,
    "person_seniority": "entry",
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/juan-cerrillo-2799009a",
    "person_city": "Chihuahua",
    "person_state": "Chihuahua",
    "person_country": "Mexico",
    "job_start_date": null,
    "current_organization_id": "['54a21fcb7468693a7e18a00e']",
    "person_linkedin_connections": "1",
    "company_name": "Jabil",
    "company_industries": "['electrical/electronic manufacturing']",
    "company_website_url": "http://www.jabil.com",
    "company_linkedin_url": "['http://www.linkedin.com/company/4852']",
    "company_location": "Florida, US",
    "company_hq_city": "Saint Petersburg",
    "company_year_founded": "1966",
    "company_funding": null,
    "company_phone": "(727) 577-9749",
    "random": "0.6361221859697253",
    "modality": "people",
    "person_name": "Juan Cerrillo",
    "relavence_boost": "0.06931471805599453",
    "person_vacuumed_at": "2016-08-23T03:23:26.167+00:00",
    "person_email_analyzed": "juan_cerrillo@jabil.com",
    "person_location_geojson": "{'type': 'envelope', 'coordinates': [[-106.073198, 28.6594105], [-106.0651177, 28.6551324]]}",
    "person_title_normalized": "produccion",
    "person_detailed_function": "produccion",
    "person_name_unanalyzed_downcase": "juan cerrillo",
    "person_location_state_with_country": "Chihuahua, Mexico",
    "person_extrapolated_email_confidence": "0.94",
    "primary_title_normalized_for_faceting": "Produccion",
    "sanitized_organization_name_unanalyzed": "jabil",
    "person_location_city_with_state_or_country": "Chihuahua, Mexico"
  },
  {
    "id": "80014",
    "person_email": "jdyer@sbgi.net",
    "person_email_status": "Extrapolated",
    "first_name": "j",
    "last_name": "dyer",
    "person_title": "Assistant Regional Controller",
    "person_function": null,
    "person_seniority": "entry",
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/j-dyer-a206346",
    "person_city": "Norfolk",
    "person_state": "Virginia",
    "person_country": "United States",
    "job_start_date": "2000-12-01T00:00:00.000Z",
    "current_organization_id": "['54a1362d69702d4b2efbd300']",
    "person_linkedin_connections": "17",
    "company_name": "Sinclair Broadcast Group",
    "company_industries": "['broadcast media']",
    "company_website_url": "http://www.sbgi.net",
    "company_linkedin_url": "['http://www.linkedin.com/company/13038']",
    "company_location": "Maryland, US",
    "company_hq_city": "Cockeysville",
    "company_year_founded": "1986",
    "company_funding": null,
    "company_phone": "(410) 568-1500",
    "random": "0.6361227503512055",
    "modality": "people",
    "person_name": "J Dyer",
    "relavence_boost": "0.28903717578961646",
    "predictive_scores": "{'551e3ef07261695147160000': 0.8221511595913151}",
    "person_vacuumed_at": "2017-01-06T14:14:42.976+00:00",
    "person_email_analyzed": "jdyer@sbgi.net",
    "person_location_geojson": "{'type': 'envelope', 'coordinates': [[-76.3455689, 36.9692977], [-76.1769049, 36.820757]]}",
    "person_title_normalized": "assistant regional controller",
    "person_detailed_function": "controller",
    "person_name_unanalyzed_downcase": "j dyer",
    "person_location_state_with_country": "Virginia, US",
    "person_extrapolated_email_confidence": "0.85",
    "primary_title_normalized_for_faceting": "Assistant Regional Controller",
    "sanitized_organization_name_unanalyzed": "sinclair broadcast group",
    "person_location_city_with_state_or_country": "Norfolk, Virginia"
  },
  {
    "id": "80013",
    "person_email": "jmoyer@underconstruction.ca",
    "person_email_status": "Extrapolated",
    "first_name": "joanne",
    "last_name": "moyer",
    "person_title": "retired",
    "person_function": null,
    "person_seniority": "entry",
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/joanne-moyer-a0aa99a7",
    "person_city": "Raleigh",
    "person_state": "North Carolina",
    "person_country": "United States",
    "job_start_date": null,
    "current_organization_id": "['54a11c6369702d84c5119b00']",
    "person_linkedin_connections": "7",
    "company_name": "Retired",
    "company_industries": "['sports']",
    "company_website_url": "http://www.underconstruction.ca",
    "company_linkedin_url": "['http://www.linkedin.com/company/1946789']",
    "company_location": null,
    "company_hq_city": null,
    "company_year_founded": null,
    "company_funding": null,
    "company_phone": "(760) 797-5763",
    "random": "0.6320971305734115",
    "modality": "people",
    "person_name": "Joanne Moyer",
    "relavence_boost": "0.2079441541679836",
    "person_vacuumed_at": "2016-08-28T21:26:13.652+00:00",
    "person_email_analyzed": "jmoyer@underconstruction.ca",
    "person_location_geojson": "{'type': 'envelope', 'coordinates': [[-78.8190489, 35.97172800000001], [-78.471063, 35.7158079]]}",
    "person_title_normalized": "retired",
    "person_detailed_function": "retired",
    "person_name_unanalyzed_downcase": "joanne moyer",
    "person_location_state_with_country": "North Carolina, US",
    "person_extrapolated_email_confidence": "0.48",
    "primary_title_normalized_for_faceting": "Retired",
    "sanitized_organization_name_unanalyzed": "retired",
    "person_location_city_with_state_or_country": "Raleigh, North Carolina"
  },
  {
    "id": "80012",
    "person_email": "anissa.evans@amnhealthcare.com",
    "person_email_status": "Extrapolated",
    "first_name": "anissa",
    "last_name": "evans",
    "person_title": "Travel Nurse",
    "person_function": null,
    "person_seniority": "entry",
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/anissa-evans-71a53850",
    "person_city": "Canton",
    "person_state": "Mississippi",
    "person_country": "United States",
    "job_start_date": null,
    "current_organization_id": "['54a1350069702d44942e5e00']",
    "person_linkedin_connections": "41",
    "company_name": "AMN Healthcare",
    "company_industries": "['staffing & recruiting']",
    "company_website_url": "http://www.amnhealthcare.com",
    "company_linkedin_url": "['http://www.linkedin.com/company/10021']",
    "company_location": "California, US",
    "company_hq_city": "San Diego",
    "company_year_founded": "1985",
    "company_funding": null,
    "company_phone": "(866) 871-8519",
    "random": "0.6320987759493822",
    "modality": "people",
    "person_name": "Anissa Evans",
    "relavence_boost": "0.37376696182833685",
    "predictive_scores": "{'551e3ef07261695147160000': 0.6403218607267547}",
    "person_vacuumed_at": "2017-12-18T01:15:25.842+00:00",
    "person_email_analyzed": "anissa.evans@amnhealthcare.com",
    "person_location_geojson": "{'type': 'envelope', 'coordinates': [[-90.09149300000001, 32.6530391], [-89.97297999999999, 32.5620519]]}",
    "person_title_normalized": "travel nurse",
    "person_detailed_function": "travel nurse",
    "person_name_unanalyzed_downcase": "anissa evans",
    "person_location_state_with_country": "Mississippi, US",
    "person_extrapolated_email_confidence": "0.67",
    "primary_title_normalized_for_faceting": "Travel Nurse",
    "sanitized_organization_name_unanalyzed": "amn healthcare",
    "person_location_city_with_state_or_country": "Canton, Mississippi"
  },
  {
    "id": "80011",
    "person_email": "susan.pucie@nih.gov",
    "person_email_status": "Extrapolated",
    "first_name": "susan",
    "last_name": "pucie",
    "person_title": "SBIR/STTR Program Coordinator",
    "person_function": null,
    "person_seniority": "entry",
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/susan-pucie-72894a1b",
    "person_city": "Washington",
    "person_state": "District of Columbia",
    "person_country": "United States",
    "job_start_date": null,
    "current_organization_id": "['55e164fcf3e5bb2c730000dd']",
    "person_linkedin_connections": "1",
    "company_name": "The National Institutes of Health",
    "company_industries": "['research']",
    "company_website_url": "http://www.nih.gov",
    "company_linkedin_url": "['http://www.linkedin.com/company/165107']",
    "company_location": "Maryland, US",
    "company_hq_city": "Bethesda",
    "company_year_founded": "1887",
    "company_funding": null,
    "company_phone": "(301) 496-4000",
    "random": "0.6320907114879124",
    "modality": "people",
    "person_name": "Susan Pucie",
    "relavence_boost": "0.26931471805599455",
    "person_vacuumed_at": "2016-08-27T11:29:08.537+00:00",
    "person_email_analyzed": "susan.pucie@nih.gov",
    "person_location_geojson": "{'type': 'envelope', 'coordinates': [[-77.1197401, 38.995548], [-76.909393, 38.8031494]]}",
    "person_title_normalized": "sbir sttr program coordinator",
    "person_detailed_function": "sbir sttr program",
    "person_name_unanalyzed_downcase": "susan pucie",
    "person_location_state_with_country": "District of Columbia, US",
    "person_extrapolated_email_confidence": "0.8",
    "primary_title_normalized_for_faceting": "Sbir Sttr Program Coordinator",
    "sanitized_organization_name_unanalyzed": "the national institutes of health",
    "person_location_city_with_state_or_country": "Washington, District of Columbia"
  },
  {
    "id": "80010",
    "person_email": "renu.rawat@progressivedigitalmedia.com",
    "person_email_status": "Extrapolated",
    "first_name": "renu",
    "last_name": "rawat",
    "person_title": null,
    "person_function": null,
    "person_seniority": null,
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/renu-rawat-986716107",
    "person_city": "Hyderabad",
    "person_state": "Telangana",
    "person_country": "India",
    "job_start_date": null,
    "current_organization_id": "['5ab09d03a6da98109f702433']",
    "person_linkedin_connections": "500",
    "company_name": "GlobalData Plc",
    "company_industries": "['information technology & services']",
    "company_website_url": "http://www.progressivedigitalmedia.com",
    "company_linkedin_url": "['http://www.linkedin.com/company/516464']",
    "company_location": "England, United Kingdom",
    "company_hq_city": "London",
    "company_year_founded": "2007",
    "company_funding": null,
    "company_phone": "+44 20 7936 6400",
    "random": "0.6320902225568259",
    "modality": "people",
    "person_name": "Renu Rawat",
    "relavence_boost": "0.8216606101084865",
    "predictive_scores": "{'551e3ef07261695147160000': 0.24779118184764198}",
    "person_vacuumed_at": "2018-02-11T07:12:46.288+00:00",
    "person_email_analyzed": "renu.rawat@progressivedigitalmedia.com",
    "person_location_geojson": "{'type': 'envelope', 'coordinates': [[78.1599217, 17.6078088], [78.6561693, 17.2168886]]}",
    "person_name_unanalyzed_downcase": "renu rawat",
    "person_location_state_with_country": "Telangana, India",
    "person_extrapolated_email_confidence": "0.86",
    "sanitized_organization_name_unanalyzed": "globaldata plc",
    "person_location_city_with_state_or_country": "Hyderabad, India"
  },
  {
    "id": "80009",
    "person_email": "jjohnson@itsne.com",
    "person_email_status": "Extrapolated",
    "first_name": "james",
    "last_name": "johnson",
    "person_title": "Systems Engineer",
    "person_function": "['engineering']",
    "person_seniority": "entry",
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/james-johnson-6333943a",
    "person_city": "Bangor",
    "person_state": "Maine",
    "person_country": "United States",
    "job_start_date": "2015-12-01T00:00:00.000Z",
    "current_organization_id": "['5592176c73696418d2ae6200']",
    "person_linkedin_connections": "47",
    "company_name": "ITS, Inc.",
    "company_industries": "['information technology & services']",
    "company_website_url": "http://www.itsne.com",
    "company_linkedin_url": "['http://www.linkedin.com/company/2381001']",
    "company_location": "Maine, US",
    "company_hq_city": "Buxton",
    "company_year_founded": "2001",
    "company_funding": null,
    "company_phone": "(207) 929-8247",
    "random": "0.632089395972778",
    "modality": "people",
    "person_name": "James Johnson",
    "relavence_boost": "0.5871201010907892",
    "person_vacuumed_at": "2017-12-14T11:54:33.000+00:00",
    "person_email_analyzed": "jjohnson@itsne.com",
    "person_location_geojson": "{'type': 'envelope', 'coordinates': [[-68.85918079999999, 44.88146889999999], [-68.7189697, 44.7778699]]}",
    "person_title_normalized": "systems engineer",
    "person_detailed_function": "systems engineer",
    "person_name_unanalyzed_downcase": "james johnson",
    "person_location_state_with_country": "Maine, US",
    "person_extrapolated_email_confidence": "0.1",
    "primary_title_normalized_for_faceting": "Systems Engineer",
    "sanitized_organization_name_unanalyzed": "its inc",
    "person_location_city_with_state_or_country": "Bangor, Maine"
  },
  {
    "id": "80008",
    "person_email": "onuorah.joseph@nlng.com",
    "person_email_status": "Extrapolated",
    "first_name": "onuorah",
    "last_name": "joseph",
    "person_title": "HOD",
    "person_function": null,
    "person_seniority": "entry",
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/onuorah-joseph-113200116",
    "person_city": null,
    "person_state": null,
    "person_country": "Nigeria",
    "job_start_date": null,
    "current_organization_id": "['54a11d6e69702d8ccc210901']",
    "person_linkedin_connections": "1",
    "company_name": "Nigeria LNG Limited",
    "company_industries": "['oil & energy']",
    "company_website_url": "http://www.nigerialng.com",
    "company_linkedin_url": "['http://www.linkedin.com/company/39386']",
    "company_location": "Rivers, Nigeria",
    "company_hq_city": "Port Harcourt",
    "company_year_founded": "1989",
    "company_funding": null,
    "company_phone": null,
    "random": "0.6320848025011221",
    "modality": "people",
    "person_name": "Onuorah Joseph",
    "relavence_boost": "0.26931471805599455",
    "person_vacuumed_at": "2017-11-03T14:31:32.000+00:00",
    "person_email_analyzed": "onuorah.joseph@nlng.com",
    "person_title_normalized": "hod",
    "person_detailed_function": "hod",
    "person_name_unanalyzed_downcase": "onuorah joseph",
    "person_extrapolated_email_confidence": "0.99",
    "primary_title_normalized_for_faceting": "Hod",
    "sanitized_organization_name_unanalyzed": "nigeria lng limited"
  },
  {
    "id": "80007",
    "person_email": "fmachuca@enjoy.cl",
    "person_email_status": "Extrapolated",
    "first_name": "francisco",
    "last_name": "machuca",
    "person_title": "Auditor",
    "person_function": null,
    "person_seniority": "entry",
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/francisco-valdebenito-machuca-8319906b",
    "person_city": null,
    "person_state": null,
    "person_country": "Chile",
    "job_start_date": null,
    "current_organization_id": "['54a23d8f7468692e716d4319']",
    "person_linkedin_connections": "6",
    "company_name": "Enjoy S.A.",
    "company_industries": "['entertainment']",
    "company_website_url": "http://www.enjoy.cl",
    "company_linkedin_url": "['http://www.linkedin.com/company/538979']",
    "company_location": null,
    "company_hq_city": null,
    "company_year_founded": "1975",
    "company_funding": null,
    "company_phone": "(600) 700-6000",
    "random": "0.6320945490178531",
    "modality": "people",
    "person_name": "Francisco Machuca",
    "relavence_boost": "0.19459101490553132",
    "person_vacuumed_at": "2017-05-10T06:14:11.000+00:00",
    "person_email_analyzed": "fmachuca@enjoy.cl",
    "person_title_normalized": "auditor",
    "person_detailed_function": "auditor",
    "person_name_unanalyzed_downcase": "francisco machuca",
    "person_extrapolated_email_confidence": "0.1",
    "primary_title_normalized_for_faceting": "Auditor",
    "sanitized_organization_name_unanalyzed": "enjoy sa"
  },
  {
    "id": "80006",
    "person_email": "khoo@sgpropertyinvestors.com",
    "person_email_status": "Extrapolated",
    "first_name": "mum",
    "last_name": "khoo",
    "person_title": "Property agent",
    "person_function": null,
    "person_seniority": "entry",
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/mum-khoo-6a206743",
    "person_city": null,
    "person_state": null,
    "person_country": "Singapore",
    "job_start_date": null,
    "current_organization_id": "['54a1354069702d3a9cd69c00']",
    "person_linkedin_connections": "1",
    "company_name": "Vida International Pte Ltd",
    "company_industries": "['real estate']",
    "company_website_url": "http://www.sgpropertyinvestors.com",
    "company_linkedin_url": "['http://www.linkedin.com/company/754454']",
    "company_location": null,
    "company_hq_city": null,
    "company_year_founded": null,
    "company_funding": null,
    "company_phone": null,
    "random": "0.6320946291089058",
    "modality": "people",
    "person_name": "Mum Khoo",
    "relavence_boost": "0.26931471805599455",
    "person_vacuumed_at": "2016-08-23T21:06:17.085+00:00",
    "person_email_analyzed": "khoo@sgpropertyinvestors.com",
    "person_title_normalized": "property agent",
    "person_detailed_function": "property agent",
    "person_name_unanalyzed_downcase": "mum khoo",
    "person_extrapolated_email_confidence": "0.6",
    "primary_title_normalized_for_faceting": "Property Agent",
    "sanitized_organization_name_unanalyzed": "vida international pte ltd"
  },
  {
    "id": "80005",
    "person_email": "jose@idb-eg.com",
    "person_email_status": "Extrapolated",
    "first_name": "jose",
    "last_name": "viegas",
    "person_title": "Analista de sistemas",
    "person_function": null,
    "person_seniority": "entry",
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/josé-leandro-viegas-8956ba122",
    "person_city": "Maringá",
    "person_state": "Paraná",
    "person_country": "Brazil",
    "job_start_date": null,
    "current_organization_id": "['56dc82f0f3e5bb2793002628']",
    "person_linkedin_connections": "7",
    "company_name": "IDB",
    "company_industries": null,
    "company_website_url": "http://www.idb-eg.com",
    "company_linkedin_url": "['http://www.linkedin.com/company/7130800']",
    "company_location": null,
    "company_hq_city": null,
    "company_year_founded": null,
    "company_funding": null,
    "company_phone": null,
    "random": "0.6320913028842832",
    "modality": "people",
    "person_name": "Jose Viegas",
    "relavence_boost": "0.4079441541679836",
    "person_vacuumed_at": "2017-11-08T15:25:10.000+00:00",
    "person_email_analyzed": "jose@idb-eg.com",
    "person_location_geojson": "{'type': 'envelope', 'coordinates': [[-52.09974279999999, -23.2700128], [-51.8414745, -23.5517934]]}",
    "person_title_normalized": "analista de sistemas",
    "person_detailed_function": "analista de sistemas",
    "person_name_unanalyzed_downcase": "jose viegas",
    "person_location_state_with_country": "Paraná, Brazil",
    "person_extrapolated_email_confidence": "0.6",
    "primary_title_normalized_for_faceting": "Analista De Sistemas",
    "sanitized_organization_name_unanalyzed": "idb",
    "person_location_city_with_state_or_country": "Maringá, Brazil"
  },
  {
    "id": "80004",
    "person_email": "inge.colombeen@ocmwgent.be",
    "person_email_status": "Extrapolated",
    "first_name": "inge",
    "last_name": "colombeen",
    "person_title": "verzorgende",
    "person_function": null,
    "person_seniority": "entry",
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/inge-colombeen-86327b86",
    "person_city": "Ghent",
    "person_state": "Flanders",
    "person_country": "Belgium",
    "job_start_date": null,
    "current_organization_id": "['54a13c1869702de5b323cf01']",
    "person_linkedin_connections": "3",
    "company_name": "OCMW Gent",
    "company_industries": "['civic & social organization']",
    "company_website_url": "http://www.ocmwgent.be",
    "company_linkedin_url": "['http://www.linkedin.com/company/52895']",
    "company_location": null,
    "company_hq_city": null,
    "company_year_founded": null,
    "company_funding": null,
    "company_phone": "+32 9 266 99 11",
    "random": "0.6320674405732983",
    "modality": "people",
    "person_name": "Inge Colombeen",
    "relavence_boost": "0.33862943611198904",
    "person_vacuumed_at": "2016-08-11T21:21:25.208+00:00",
    "person_email_analyzed": "inge.colombeen@ocmwgent.be",
    "person_location_geojson": "{'type': 'envelope', 'coordinates': [[3.57974, 51.1880501], [3.8492301, 50.97683]]}",
    "person_title_normalized": "verzorgende",
    "person_detailed_function": "verzorgende",
    "person_name_unanalyzed_downcase": "inge colombeen",
    "person_location_state_with_country": "Flanders, Belgium",
    "person_extrapolated_email_confidence": "0.99",
    "primary_title_normalized_for_faceting": "Verzorgende",
    "sanitized_organization_name_unanalyzed": "ocmw gent",
    "person_location_city_with_state_or_country": "Ghent, Belgium"
  },
  {
    "id": "80003",
    "person_email": "russell.priest@gloucestershire.gov.uk",
    "person_email_status": "Extrapolated",
    "first_name": "russell",
    "last_name": "priest",
    "person_title": "Aerial Archaeologist",
    "person_function": null,
    "person_seniority": "entry",
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/russell-priest-8a052642",
    "person_city": "Swindon",
    "person_state": "England",
    "person_country": "United Kingdom",
    "job_start_date": "2007-01-01T00:00:00.000Z",
    "current_organization_id": "['54a12af369702d979c426302']",
    "person_linkedin_connections": "14",
    "company_name": "Gloucestershire County Council",
    "company_industries": "['government administration']",
    "company_website_url": "http://www.gloucestershire.gov.uk",
    "company_linkedin_url": "['http://www.linkedin.com/company/38977']",
    "company_location": "England, United Kingdom",
    "company_hq_city": "Gloucester",
    "company_year_founded": null,
    "company_funding": null,
    "company_phone": "+44 1452 425000",
    "random": "0.6320789527980591",
    "modality": "people",
    "person_name": "Russell Priest",
    "relavence_boost": "0.270805020110221",
    "predictive_scores": "{'551e3ef07261695147160000': 0.34982788032944256}",
    "person_vacuumed_at": "2017-09-22T03:13:59.000+00:00",
    "person_email_analyzed": "russell.priest@gloucestershire.gov.uk",
    "person_location_geojson": "{'type': 'envelope', 'coordinates': [[-1.8651375, 51.6091293], [-1.7194727, 51.530334]]}",
    "person_title_normalized": "aerial archaeologist",
    "person_detailed_function": "aerial archaeologist",
    "person_name_unanalyzed_downcase": "russell priest",
    "person_location_state_with_country": "England, United Kingdom",
    "person_extrapolated_email_confidence": "0.87",
    "primary_title_normalized_for_faceting": "Aerial Archaeologist",
    "sanitized_organization_name_unanalyzed": "gloucestershire county council",
    "person_location_city_with_state_or_country": "Swindon, United Kingdom"
  },
  {
    "id": "80002",
    "person_email": "kristian.engum@if.se",
    "person_email_status": "Extrapolated",
    "first_name": "kristian",
    "last_name": "engum",
    "person_title": "Rådgiver",
    "person_function": null,
    "person_seniority": "entry",
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/kristian-engum-482847a0",
    "person_city": "Bergen",
    "person_state": "Hordaland",
    "person_country": "Norway",
    "job_start_date": null,
    "current_organization_id": "['54a134af69702d4494753500']",
    "person_linkedin_connections": "2",
    "company_name": "If Insurance (If P&C Insurance)",
    "company_industries": "['insurance']",
    "company_website_url": "http://www.if.se",
    "company_linkedin_url": "['http://www.linkedin.com/company/5333']",
    "company_location": "Stockholms län, Sweden",
    "company_hq_city": null,
    "company_year_founded": "1999",
    "company_funding": null,
    "company_phone": "+46 77 165 56 55",
    "random": "0.6320762205839465",
    "modality": "people",
    "person_name": "Kristian Engum",
    "relavence_boost": "0.10986122886681099",
    "person_vacuumed_at": "2017-10-29T15:20:45.000+00:00",
    "person_email_analyzed": "kristian.engum@if.se",
    "person_location_geojson": "{'type': 'envelope', 'coordinates': [[5.170590199999999, 60.52683889999999], [5.6877642, 60.2035413]]}",
    "person_title_normalized": "rådgiver",
    "person_detailed_function": "rådgiver",
    "person_name_unanalyzed_downcase": "kristian engum",
    "person_location_state_with_country": "Hordaland, Norway",
    "person_extrapolated_email_confidence": "0.99",
    "primary_title_normalized_for_faceting": "Rådgiver",
    "sanitized_organization_name_unanalyzed": "if insurance if pc insurance",
    "person_location_city_with_state_or_country": "Bergen, Norway"
  },
  {
    "id": "80001",
    "person_email": "plaplante@kennesaw.edu",
    "person_email_status": "Extrapolated",
    "first_name": "phillip",
    "last_name": "laplante",
    "person_title": "Student",
    "person_function": null,
    "person_seniority": "entry",
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/laplantephillip",
    "person_city": "Cumming",
    "person_state": "Georgia",
    "person_country": "United States",
    "job_start_date": null,
    "current_organization_id": "['54a139cb69702de5b3fb6500']",
    "person_linkedin_connections": "22",
    "company_name": "Kennesaw State University",
    "company_industries": "['higher education']",
    "company_website_url": "http://www.kennesaw.edu",
    "company_linkedin_url": "['http://www.linkedin.com/company/9801']",
    "company_location": "Georgia, US",
    "company_hq_city": "Kennesaw",
    "company_year_founded": "1963",
    "company_funding": null,
    "company_phone": "(470) 578-6000",
    "random": "0.6320711736393536",
    "modality": "people",
    "person_name": "Phillip Laplante",
    "relavence_boost": "0.5135494215929151",
    "predictive_scores": "{'551e3ef07261695147160000': 0.32280234058294455}",
    "person_vacuumed_at": "2018-01-23T23:11:41.144+00:00",
    "person_email_analyzed": "plaplante@kennesaw.edu",
    "person_location_geojson": "{'type': 'envelope', 'coordinates': [[-84.157217, 34.2335089], [-84.10404199999999, 34.17355500000001]]}",
    "person_title_normalized": "student",
    "person_detailed_function": "student",
    "person_name_unanalyzed_downcase": "phillip laplante",
    "person_location_state_with_country": "Georgia, US",
    "person_extrapolated_email_confidence": "0.55",
    "primary_title_normalized_for_faceting": "Student",
    "sanitized_organization_name_unanalyzed": "kennesaw state university",
    "person_location_city_with_state_or_country": "Cumming, Georgia"
  },
  {
    "id": "80000",
    "person_email": "kpradhan@nwr7.com",
    "person_email_status": "Extrapolated",
    "first_name": "kim",
    "last_name": "pradhan",
    "person_title": "Special Education Middle School Teacher",
    "person_function": "['education']",
    "person_seniority": "entry",
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/kim-pradhan-7a4b452b",
    "person_city": "Hartford",
    "person_state": "Connecticut",
    "person_country": "United States",
    "job_start_date": null,
    "current_organization_id": "['54a13abe69702dac841a0501']",
    "person_linkedin_connections": "16",
    "company_name": "Northwestern Regional High School",
    "company_industries": "['primary/secondary education']",
    "company_website_url": "http://www.nwr7.com",
    "company_linkedin_url": "['http://www.linkedin.com/company/765997']",
    "company_location": "Connecticut, US",
    "company_hq_city": null,
    "company_year_founded": null,
    "company_funding": null,
    "company_phone": "(860) 379-8525",
    "random": "0.6320186506491154",
    "modality": "people",
    "person_name": "Kim Pradhan",
    "relavence_boost": "0.2833213344056216",
    "predictive_scores": "{'551e3ef07261695147160000': 0.2816892796654214}",
    "person_vacuumed_at": "2017-10-20T22:25:18.000+00:00",
    "person_email_analyzed": "kpradhan@nwr7.com",
    "person_location_geojson": "{'type': 'envelope', 'coordinates': [[-72.717638, 41.8075751], [-72.6425359, 41.7238]]}",
    "person_title_normalized": "special education middle school teacher",
    "person_detailed_function": "special education middle school teacher",
    "person_name_unanalyzed_downcase": "kim pradhan",
    "person_location_state_with_country": "Connecticut, US",
    "person_extrapolated_email_confidence": "0.6",
    "primary_title_normalized_for_faceting": "Special Education Middle School Teacher",
    "sanitized_organization_name_unanalyzed": "northwestern regional high school",
    "person_location_city_with_state_or_country": "Hartford, Connecticut"
  },
  {
    "id": "79999",
    "person_email": "cjimenez@ssasis.com",
    "person_email_status": "Extrapolated",
    "first_name": "carlos",
    "last_name": "jimenez",
    "person_title": "Asistente administrativo",
    "person_function": null,
    "person_seniority": "entry",
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/carlos-jim%c3%a9nez-26256a33",
    "person_city": null,
    "person_state": null,
    "person_country": "Panama",
    "job_start_date": null,
    "current_organization_id": "['54a1356569702d46f01c8e00']",
    "person_linkedin_connections": "1",
    "company_name": "SSA Sistemas",
    "company_industries": "['information technology & services']",
    "company_website_url": "http://www.ssasis.com",
    "company_linkedin_url": "['http://www.linkedin.com/company/147393']",
    "company_location": "Panamá, Panama",
    "company_hq_city": "Panamá",
    "company_year_founded": "1997",
    "company_funding": null,
    "company_phone": "(507) 294-7500",
    "random": "0.6319865949232094",
    "modality": "people",
    "person_name": "Carlos Jimenez",
    "relavence_boost": "0.06931471805599453",
    "person_vacuumed_at": "2016-12-07T10:00:12.000+00:00",
    "person_email_analyzed": "cjimenez@ssasis.com",
    "person_title_normalized": "asistente administrativo",
    "person_detailed_function": "asistente administrativo",
    "person_name_unanalyzed_downcase": "carlos jimenez",
    "person_extrapolated_email_confidence": "0.6",
    "primary_title_normalized_for_faceting": "Asistente Administrativo",
    "sanitized_organization_name_unanalyzed": "ssa sistemas"
  },
  {
    "id": "79998",
    "person_email": "jose.rio@senior.com.br",
    "person_email_status": "Extrapolated",
    "first_name": "jose",
    "last_name": "rio",
    "person_title": "Director",
    "person_function": null,
    "person_seniority": "director",
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/jose-antonio-del-rio-98202270",
    "person_city": "Naucalpan de Juárez",
    "person_state": "Estado de México",
    "person_country": "Mexico",
    "job_start_date": null,
    "current_organization_id": "['54a1268769702d8eebaf1300']",
    "person_linkedin_connections": "42",
    "company_name": "Senior Sistemas",
    "company_industries": "['computer software']",
    "company_website_url": "http://www.senior.com.br",
    "company_linkedin_url": "['http://www.linkedin.com/company/27123']",
    "company_location": "Santa Catarina, Brazil",
    "company_hq_city": "Blumenau",
    "company_year_founded": "1988",
    "company_funding": null,
    "company_phone": "+55 47 3221-3300",
    "random": "0.6320189019489584",
    "modality": "people",
    "person_name": "Jose Rio",
    "relavence_boost": "0.37612001156935626",
    "predictive_scores": "{'551e3ef07261695147160000': 0.16867116277079042}",
    "person_vacuumed_at": "2017-02-24T06:01:30.595+00:00",
    "person_email_analyzed": "jose.rio@senior.com.br",
    "person_location_geojson": "{'type': 'envelope', 'coordinates': [[-99.2398642, 19.4825548], [-99.2339013, 19.4759013]]}",
    "person_title_normalized": "director",
    "person_detailed_function": "director",
    "person_name_unanalyzed_downcase": "jose rio",
    "person_location_state_with_country": "Estado de México, Mexico",
    "person_extrapolated_email_confidence": "0.93",
    "primary_title_normalized_for_faceting": "Director",
    "sanitized_organization_name_unanalyzed": "senior sistemas",
    "person_location_city_with_state_or_country": "Naucalpan de Juárez, Mexico"
  },
  {
    "id": "79997",
    "person_email": "babu@techmahindra.com",
    "person_email_status": "Verified",
    "first_name": "sunu",
    "last_name": "babu",
    "person_title": "Manager",
    "person_function": null,
    "person_seniority": "manager",
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/sunu-babu-07353320",
    "person_city": "Pune",
    "person_state": "Maharashtra",
    "person_country": "India",
    "job_start_date": "2008-12-01T00:00:00.000Z",
    "current_organization_id": "['54a11cdf69702d8ccc3dd100']",
    "person_linkedin_connections": "149",
    "company_name": "Tech Mahindra",
    "company_industries": "['information technology & services']",
    "company_website_url": "http://www.techmahindra.com",
    "company_linkedin_url": "['http://www.linkedin.com/company/3067']",
    "company_location": "Maharashtra, India",
    "company_hq_city": "Pune",
    "company_year_founded": "1986",
    "company_funding": null,
    "company_phone": null,
    "random": "0.6320190744196518",
    "modality": "people",
    "person_name": "Sunu Babu",
    "relavence_boost": "0.9010635294096256",
    "predictive_scores": "{'551e3ef07261695147160000': 0.9615344636775522}",
    "person_vacuumed_at": "2017-10-13T15:22:43.000+00:00",
    "person_email_analyzed": "babu@techmahindra.com",
    "person_location_geojson": "{'type': 'envelope', 'coordinates': [[73.7865989, 18.475005], [73.8162351, 18.452386]]}",
    "person_title_normalized": "manager",
    "person_detailed_function": "manager",
    "person_name_unanalyzed_downcase": "sunu babu",
    "person_location_state_with_country": "Maharashtra, India",
    "person_extrapolated_email_confidence": "0.72",
    "primary_title_normalized_for_faceting": "Manager",
    "sanitized_organization_name_unanalyzed": "tech mahindra",
    "person_location_city_with_state_or_country": "Pune, India"
  },
  {
    "id": "79996",
    "person_email": "jlifshay@sfaf.org",
    "person_email_status": "Verified",
    "first_name": "julie",
    "last_name": "lifshay",
    "person_title": "Director, Health Navigation",
    "person_function": null,
    "person_seniority": "director",
    "person_department": null,
    "person_phone": null,
    "person_linkedin_url": "http://www.linkedin.com/in/julie-lifshay-73084959",
    "person_city": "Oakland",
    "person_state": "California",
    "person_country": "United States",
    "job_start_date": "2015-11-01T00:00:00.000Z",
    "current_organization_id": "['54a23c207468693a7e326018', '556d0f9c7369641266a48600']",
    "person_linkedin_connections": "443",
    "company_name": null,
    "company_industries": null,
    "company_website_url": null,
    "company_linkedin_url": null,
    "company_location": null,
    "company_hq_city": null,
    "company_year_founded": null,
    "company_funding": null,
    "company_phone": null,
    "random": "0.6319979835272646",
    "modality": "people",
    "person_name": "Julie Lifshay",
    "relavence_boost": "1.0095824562432225",
    "predictive_scores": "{'551e3ef07261695147160000': 0.26625659588257955}",
    "person_vacuumed_at": "2017-11-25T09:55:39.000+00:00",
    "person_email_analyzed": "jlifshay@sfaf.org",
    "prospected_by_team_ids": [
      "581cce477ff0bb49a3b6efa0",
      "583483445dd1bacc8408b123",
      "5abc21f07dda4d196a7736a7"
    ],
    "person_location_geojson": "{'type': 'envelope', 'coordinates': [[-122.3426648, 37.88472489999999], [-122.1149234, 37.699198]]}",
    "person_title_normalized": "director health navigation",
    "person_detailed_function": "health navigation",
    "person_name_unanalyzed_downcase": "julie lifshay",
    "person_location_state_with_country": "California, US",
    "person_extrapolated_email_confidence": "0.95",
    "primary_title_normalized_for_faceting": "Director Health Navigation",
    "sanitized_organization_name_unanalyzed": "san francisco aids foundation",
    "person_location_city_with_state_or_country": "Oakland, California"
  }
]

